import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/feature/SeoHead';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.diariode1trader.com';

const contactosSchema = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contactos | Diário de 1 Trader',
  url: `${siteUrl}/contactos`,
  description: 'Entre em contacto com a Diário de 1 Trader para dúvidas sobre prop firms, estratégias de trading ou parcerias de afiliados.',
  mainEntity: {
    '@type': 'Organization',
    name: 'Diário de 1 Trader',
    url: siteUrl,
    email: 'info@diariode1trader.com',
    sameAs: ['https://www.youtube.com/@diario1trader'],
  },
};

export default function ContactosPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.message.length > 500) return;
    setStatus('sending');
    try {
      const body = new URLSearchParams(form as Record<string, string>);
      await fetch('https://readdy.ai/api/form/d7d53l1noju8nk5a14lg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setStatus('success');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#f0f6ff]">
      <SeoHead
        title="Contactos | Diário de 1 Trader"
        description="Entre em contacto com a Diário de 1 Trader. Dúvidas sobre prop firms, estratégias de trading ou parcerias? Respondemos em menos de 24 horas."
        keywords="contacto, diário de 1 trader, suporte, parcerias afiliados trading"
        canonical="/contactos"
        schema={contactosSchema}
      />
      <Navbar />
      <div className="pt-28 pb-20 max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 text-xs font-semibold px-4 py-2 rounded-full mb-4">
            <i className="ri-mail-line"></i>Contactos
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-[#0d1f3c] mb-4">Fale Connosco</h1>
          <p className="text-slate-500 max-w-xl mx-auto">Tem alguma dúvida sobre prop firms, estratégias ou parcerias de afiliados? Estamos aqui para ajudar.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="space-y-6">
            {[
              { icon: 'ri-mail-send-line', title: 'Email', value: 'info@diariode1trader.com', link: 'mailto:info@diariode1trader.com' },
              { icon: 'ri-telegram-line', title: 'Telegram', value: '@FuturosPro', link: '#' },
              { icon: 'ri-youtube-line', title: 'YouTube', value: '@diario1trader', link: 'https://www.youtube.com/@diario1trader' },
            ].map((c) => (
              <a key={c.title} href={c.link} className="flex items-center gap-4 bg-white rounded-2xl p-5 border border-blue-100 hover:border-blue-300 transition-all cursor-pointer group">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 group-hover:bg-blue-600 rounded-xl transition-all">
                  <i className={`${c.icon} text-blue-600 group-hover:text-white text-xl transition-all`}></i>
                </div>
                <div>
                  <div className="text-xs text-slate-400">{c.title}</div>
                  <div className="font-semibold text-[#0d1f3c]">{c.value}</div>
                </div>
              </a>
            ))}
            <div className="bg-blue-600 rounded-2xl p-6 text-white">
              <h3 className="font-bold mb-2">Parcerias de Afiliados</h3>
              <p className="text-blue-100 text-sm leading-relaxed">É uma prop firm e quer ser analisada no nosso site? Entre em contacto para discutir possíveis parcerias.</p>
            </div>
          </div>

          <div className="lg:col-span-2 bg-white rounded-2xl border border-blue-100 p-8">
            {status === 'success' ? (
              <div className="h-full flex flex-col items-center justify-center text-center gap-4 py-16">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full">
                  <i className="ri-checkbox-circle-line text-green-500 text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold text-[#0d1f3c]">Mensagem Enviada!</h3>
                <p className="text-slate-500">Respondemos em menos de 24 horas. Obrigado pelo contacto.</p>
                <button onClick={() => setStatus('idle')} className="mt-2 bg-blue-600 text-white font-semibold px-6 py-2.5 rounded-full text-sm cursor-pointer whitespace-nowrap">Nova Mensagem</button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Nome *</label>
                    <input name="name" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-500 transition-all" placeholder="O seu nome" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email *</label>
                    <input name="email" type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-500 transition-all" placeholder="email@exemplo.com" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Assunto *</label>
                  <input name="subject" required value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-500 transition-all" placeholder="Ex: Dúvida sobre FTMO" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Mensagem * <span className="text-slate-400 font-normal">({form.message.length}/500)</span></label>
                  <textarea name="message" required maxLength={500} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={5} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-500 transition-all resize-none" placeholder="Descreva a sua questão..." />
                  {form.message.length > 500 && <p className="text-red-500 text-xs mt-1">Máximo 500 caracteres.</p>}
                </div>
                {status === 'error' && <p className="text-red-500 text-sm">Erro ao enviar. Tente novamente.</p>}
                <button type="submit" disabled={status === 'sending' || form.message.length > 500} className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold py-3.5 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap">
                  {status === 'sending' ? 'A enviar...' : 'Enviar Mensagem'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
